import React, { useEffect, useRef, useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { t, interpolate } from "../translations";
import { cardLocales } from "../translations/cardLocales";
import { InfoPopover } from "./InfoPopover";
import {
  activeBadges,
  flattenProductCardItems,
  toPtAmount,
} from "../utils/productCards";
import "./ProductCardStrip.css";

/**
 * The widget's product card, on the operator's screen.
 *
 * Ported from ai-agent-widget app/src/renderers/product-card-renderer.tsx and
 * app/src/ui/card-carousel.tsx. The point is that an operator reading a
 * transcript sees the card the shopper actually saw, so the geometry, the
 * wording and the price treatment are reproduced rather than reinterpreted.
 *
 * Dropped on purpose, because the backoffice is read only:
 *  - the add to cart button and everything that exists only for it (the
 *    buy_button_active flag, the postback action, the cart callback).
 *
 * The price info popover, which renders the tenant's legal price disclaimer,
 * IS ported (RPAA-205 follow up): it reaches the widget through
 * `config.copy.priceInfoNotice` on the per chat widget config, which
 * ConversationDetail already fetches via useChatWidgetConfig for the theme
 * and the card locale, so it travels the same way, not fetched here.
 *
 * Also dropped: the per chat feature gating. The widget hides badges, the
 * delivery line and the hover image unless RendererContext.productCardFeatures
 * turns each on for that chat, and the backoffice has no such runtime context.
 * Rather than invent a default, this renders EVERY field the stored card
 * carries. The consequence is honest: a transcript may show a badge that the
 * shopper's chat had switched off. The alternative, hiding data that is right
 * there in the payload, is worse for someone auditing a conversation.
 */

// Width of one card (220px, see ProductCardStrip.css) + the 8px track gap.
// One nav click scrolls by exactly one card, so the arithmetic below breaks if
// either number changes without the other.
const PRODUCT_CARD_STEP_PX = 220 + 8;

// Pixels the pointer must move before we treat it as a drag rather than a
// click on a slide. Below this threshold, clicks on the card's link still fire.
const DRAG_THRESHOLD_PX = 5;

const DRAGGING_CLASS = "kodly-ai-agent-card-carousel__track--dragging";

// CSS custom property names accept [A-Za-z0-9_-] safely; any other character
// in a (wire shaped, untrusted) kind falls back to the base badge tokens
// instead of being interpolated into a style value.
const CSS_SAFE_KIND = /^[a-z0-9_-]+$/i;

// Price derived kinds render as a chip anchored to the photo's corner, not in
// the top left overlay: on radiopopular.pt the tag zone carries campaign
// phrasing only, while the percentage lives in a box next to the price
// ("-27% sobre PVPR"). Mixing them also reads a PVPR relative percentage,
// legally NOT a price reduction, as if it were one (Omnibus).
const PRICE_BADGE_KINDS = ["discount", "discount_pvpr"];

// Context line under the chip's percentage, per kind. A kind without an entry
// renders label only.
const PRICE_BADGE_CONTEXT_KEYS = {
  discount_pvpr: "productCardPriceBadgeOverRecommended",
  discount: "productCardPriceBadgeDirect",
};

// Availability kinds with consumer copy. The delivery slot is really the
// fulfillment line: radiopopular.pt renders "Temporariamente indisponivel" and
// "Entrega na data de lancamento" exactly where the estimate would sit. The
// preorder copy is dateless because the launch date never reaches the feed. A
// kind missing here falls back to the delivery estimate, never to a guess.
const AVAILABILITY_COPY_KEYS = {
  temporarily_unavailable: "productCardAvailabilityTemporarilyUnavailable",
  preorder: "productCardAvailabilityPreorder",
};

const isPriceBadge = (badge) => PRICE_BADGE_KINDS.indexOf(badge.kind) !== -1;

// The per kind lookup rides on two inline CUSTOM PROPERTIES resolved by the
// stylesheet, instead of inline background/color declarations, so a per kind
// theme token restyles both badge surfaces from CSS alone.
function badgeStyle(kind) {
  if (!CSS_SAFE_KIND.test(kind)) return undefined;
  return {
    "--kodly-ai-agent-badge-dyn-bg": `var(--kodly-ai-agent-badge-${kind}-bg, var(--kodly-ai-agent-badge-bg))`,
    "--kodly-ai-agent-badge-dyn-text": `var(--kodly-ai-agent-badge-${kind}-text, var(--kodly-ai-agent-badge-text))`,
  };
}

function deliveryText(delivery, translate) {
  // Calendar time (business_days false) is an UPPER BOUND per the RPAA-199
  // contract (Radio Popular's "30/31" renders "ate 30 dias" on their own
  // site): hedge it, never state "{N} dias" flat, which is the RPAA-196 class
  // of bug. Applies to any min/max, so a future min<max calendar producer
  // stays hedged too.
  if (!delivery.business_days) {
    return delivery.max_days === 1
      ? translate("productCardDeliveryUpToOne")
      : translate("productCardDeliveryUpTo", { max: delivery.max_days });
  }
  // min == max collapses to a single number: the agent prose keeps "{min}/
  // {max}" even for 3/3, but "3 a 3 dias uteis" reads as a glitch on a card. A
  // business day point estimate is a genuine feed assertion, not a false
  // absolute (those are calendar upper bounds stated flat).
  if (delivery.min_days === delivery.max_days) {
    return delivery.min_days === 1
      ? translate("productCardDeliveryBusinessDaysOne")
      : translate("productCardDeliveryBusinessDaysExact", {
          days: delivery.min_days,
        });
  }
  return translate("productCardDeliveryBusinessDays", {
    min: delivery.min_days,
    max: delivery.max_days,
  });
}

/**
 * Horizontal strip with drag, nav arrows and keyboard scrolling.
 *
 * Ported from the widget's generic CardCarousel; kept private to this file
 * because the product card is the only card the transcript renders today.
 */
function CardCarousel({
  itemCount,
  cardStepPx,
  navLabels,
  ariaLabel,
  trackClass,
  children,
}) {
  const trackRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const isCarousel = itemCount > 1;

  useEffect(() => {
    if (!isCarousel) return undefined;
    const el = trackRef.current;
    if (!el) return undefined;
    const update = () => {
      setCanPrev(el.scrollLeft > 1);
      // -1px tolerance for sub pixel rounding on the right edge.
      setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, [isCarousel, itemCount]);

  // Pointer drag to scroll. Mouse only: touch already has native momentum
  // scroll and hijacking it with JS makes it feel worse. The threshold lets
  // quick clicks fall through to the card's link.
  useEffect(() => {
    if (!isCarousel) return undefined;
    const el = trackRef.current;
    if (!el) return undefined;

    let drag = null;
    let suppressNextClick = false;
    let snapRaf = null;

    const cancelSnap = () => {
      if (snapRaf !== null) {
        cancelAnimationFrame(snapRaf);
        snapRaf = null;
      }
      el.classList.remove(DRAGGING_CLASS);
    };

    // Custom snap animation. Native scrollTo({behavior:"smooth"}) uses the
    // browser's default duration (~500ms) and a near linear curve which feels
    // heavy after a flick gesture. rAF plus ease-out gives the "let go and it
    // lands" feel of native carousels. .--dragging (and so
    // scroll-snap-type:none) stays ON during the animation to stop the
    // browser's mandatory snap from fighting our scrollLeft writes mid frame.
    const animateSnap = (target, durationMs) => {
      const startScroll = el.scrollLeft;
      const distance = target - startScroll;
      if (Math.abs(distance) < 1) {
        el.scrollLeft = target;
        el.classList.remove(DRAGGING_CLASS);
        return;
      }
      const startTime = performance.now();
      // ease-out-cubic: fast initial movement, gentle settle.
      const ease = (t) => 1 - Math.pow(1 - t, 3);
      const tick = (now) => {
        const t = Math.min((now - startTime) / durationMs, 1);
        if (t >= 1) {
          // Snap to the exact target on the final frame. Do not rely on the
          // `startScroll + distance * 1` arithmetic being bit exact: the
          // browser may store scrollLeft as a sub pixel float, which then
          // fights mandatory snap when --dragging is removed below.
          el.scrollLeft = target;
          snapRaf = null;
          el.classList.remove(DRAGGING_CLASS);
        } else {
          el.scrollLeft = startScroll + distance * ease(t);
          snapRaf = requestAnimationFrame(tick);
        }
      };
      snapRaf = requestAnimationFrame(tick);
    };

    const onPointerDown = (e) => {
      if (e.pointerType !== "mouse") return;
      if (e.button !== 0) return;
      // Grabbing the strip mid snap cancels the animation and starts a fresh
      // drag from the current position.
      cancelSnap();
      drag = {
        startX: e.clientX,
        startScrollLeft: el.scrollLeft,
        pointerId: e.pointerId,
        dragged: false,
        lastX: e.clientX,
        lastTime: performance.now(),
        velocity: 0,
      };
    };

    const onPointerMove = (e) => {
      if (!drag || e.pointerId !== drag.pointerId) return;
      const dx = e.clientX - drag.startX;
      if (!drag.dragged && Math.abs(dx) > DRAG_THRESHOLD_PX) {
        drag.dragged = true;
        try {
          el.setPointerCapture(drag.pointerId);
        } catch (err) {
          /* no-op */
        }
        el.classList.add(DRAGGING_CLASS);
      }
      if (drag.dragged) {
        el.scrollLeft = drag.startScrollLeft - dx;
        e.preventDefault();
        // EMA smoothed velocity (px/ms). Smoothing avoids a 1 frame jitter at
        // the end of the gesture being read as the release velocity.
        const now = performance.now();
        const dtMs = now - drag.lastTime;
        if (dtMs > 0) {
          const v = (e.clientX - drag.lastX) / dtMs;
          drag.velocity = drag.velocity * 0.7 + v * 0.3;
        }
        drag.lastX = e.clientX;
        drag.lastTime = now;
      }
    };

    // Read the actual slide offsetLefts from the DOM. Computing the target
    // arithmetically as `idx * cardStepPx` works ONLY when flex/gap layout
    // produces exactly that step in pixels, and sub pixel rounding can leave
    // the browser's real snap points off by a fraction. When the post
    // animation `--dragging` removal restores `scroll-snap-type: mandatory`,
    // the browser then "corrects" the position by re-snapping, which is the
    // end of animation flash.
    const readSnapOffsets = () =>
      Array.prototype.map.call(el.children, (c) => c.offsetLeft);

    const endDrag = (e) => {
      if (!drag || e.pointerId !== drag.pointerId) return;
      if (drag.dragged) {
        try {
          el.releasePointerCapture(drag.pointerId);
        } catch (err) {
          /* no-op */
        }
        suppressNextClick = true;
        // Velocity biased target. A flick (|v| > FLICK) jumps one card in the
        // direction of motion even if the drag did not pass the midpoint.
        // Sign convention: positive velocity = cursor moved right = wants the
        // PREVIOUS card (lower scrollLeft); negative = wants the next.
        const FLICK = 0.4; // px/ms, about 400 px/s
        const offsets = readSnapOffsets();
        const current = el.scrollLeft;
        let target;
        if (offsets.length === 0) {
          target = current;
        } else if (drag.velocity < -FLICK) {
          const forward = offsets.find((o) => o > current + 0.5);
          target = forward === undefined ? offsets[offsets.length - 1] : forward;
        } else if (drag.velocity > FLICK) {
          const back = offsets.slice().reverse().find((o) => o < current - 0.5);
          target = back === undefined ? offsets[0] : back;
        } else {
          target = offsets.reduce((best, o) =>
            Math.abs(o - current) < Math.abs(best - current) ? o : best
          );
        }
        // Faster flick, snappier animation: 280ms when released idle, about
        // 160ms after a hard flick.
        const speed = Math.min(Math.abs(drag.velocity) / 1.5, 1);
        animateSnap(target, 280 - speed * 120);
      }
      drag = null;
    };

    const onClickCapture = (e) => {
      if (suppressNextClick) {
        e.preventDefault();
        e.stopPropagation();
        suppressNextClick = false;
      }
    };

    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerup", endDrag);
    el.addEventListener("pointercancel", endDrag);
    el.addEventListener("click", onClickCapture, true);

    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", endDrag);
      el.removeEventListener("pointercancel", endDrag);
      el.removeEventListener("click", onClickCapture, true);
      if (snapRaf !== null) cancelAnimationFrame(snapRaf);
    };
  }, [isCarousel]);

  if (itemCount === 0) return null;

  const scrollByCard = (dir) => {
    if (trackRef.current) {
      trackRef.current.scrollBy({ left: dir * cardStepPx, behavior: "smooth" });
    }
  };

  // Accepted trade off: an arrow press bubbling from a focused card link
  // scrolls the strip while DOM focus stays put on that (possibly now off
  // screen) link. Full APG carousel focus management is out of scope for this
  // intentionally minimal pattern.
  const onKeyDown = (e) => {
    if (!isCarousel) return;
    if (e.key === "ArrowRight") {
      e.preventDefault();
      scrollByCard(1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      scrollByCard(-1);
    }
  };

  const regionProps =
    isCarousel && ariaLabel ? { role: "region", "aria-label": ariaLabel } : {};

  return (
    <div
      className={`kodly-ai-agent-card-carousel${
        isCarousel ? " kodly-ai-agent-card-carousel--carousel" : ""
      }`}
    >
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
      <div
        ref={trackRef}
        className={`kodly-ai-agent-card-carousel__track${
          isCarousel ? " kodly-ai-agent-card-carousel__track--carousel" : ""
        }${trackClass ? ` ${trackClass}` : ""}`}
        tabIndex={isCarousel ? 0 : -1}
        onKeyDown={onKeyDown}
        {...regionProps}
      >
        {children}
      </div>
      {isCarousel && canPrev && (
        <button
          type="button"
          className="kodly-ai-agent-card-carousel__nav kodly-ai-agent-card-carousel__nav--prev"
          aria-label={navLabels.previous}
          onClick={() => scrollByCard(-1)}
        >
          &lsaquo;
        </button>
      )}
      {isCarousel && canNext && (
        <button
          type="button"
          className="kodly-ai-agent-card-carousel__nav kodly-ai-agent-card-carousel__nav--next"
          aria-label={navLabels.next}
          onClick={() => scrollByCard(1)}
        >
          &rsaquo;
        </button>
      )}
    </div>
  );
}

// Render a price with the cents portion as a smaller, raised superscript, the
// canonical treatment on radiopopular.pt and most PT retail product pages
// (`€499,⁹⁹`). The regex greedy anchors against the trailing two digit cents
// so any thousands separators earlier in the string (PT `€1.499,99` or US
// `€1,234.99`) stay attached to the integer part. Inputs that do not end in a
// decimal style amount, "Gratis" or "A consulta", fall through unchanged.
//
// The decimal separator is normalised to a comma since every tenant on this
// surface is PT locale; a non PT tenant would need this gated on the locale.
function PriceText({ value }) {
  const match = toPtAmount(value).match(/^(.*)[.,](\d{2})(\D*)$/);
  if (!match) return <>{value}</>;
  return (
    <>
      {match[1]},
      <span className="kodly-ai-agent-product-card__price-cents">{match[2]}</span>
      {match[3]}
    </>
  );
}

// The card's second photo, revealed while the pointer rests on the primary one.
//
// Three choices carried over from the widget:
//  - Always images[1], never a "best" pick. A quarter of Radio Popular's
//    second photos are near identical to the first, so some hovers do read as
//    a no-op; picking a more different one means downloading and comparing
//    images.
//  - The photo is fetched on FIRST hover, not upfront, so a strip of cards
//    costs what it costs today until someone shows interest. Once mounted it
//    stays, so later hovers are instant.
//  - Gated on a device that actually hovers.
function HoverSwapImage({ images, title, onError }) {
  const [mounted, setMounted] = useState(false);
  const [shown, setShown] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [swapFailed, setSwapFailed] = useState(false);
  return (
    <>
      <img
        className="kodly-ai-agent-product-card__img"
        src={images[0]}
        alt={title}
        onError={onError}
        onPointerEnter={() => {
          // Read the capability per hover rather than per mount, so a tablet
          // that gets a trackpad mid session starts swapping and a card that
          // never gets hovered never asks.
          if (
            !window.matchMedia ||
            !window.matchMedia("(hover: hover)").matches
          ) {
            return;
          }
          setMounted(true);
          setShown(true);
        }}
        onPointerLeave={() => setShown(false)}
      />
      {/* aria-hidden: a screen reader already has the product name from the
       * primary image and from the link's label, this is the same product from
       * another angle, not new information. A second photo that fails to load
       * unmounts itself, leaving the card exactly as it was.
       *
       * Revealed on `shown && loaded`, never on `shown` alone: the layer is
       * opaque, so showing it before its bytes arrive paints a blank frame over
       * the product, and a node inserted at its final opacity does not
       * transition, so the cross fade would never run on the first hover. */}
      {mounted && !swapFailed && (
        <img
          className="kodly-ai-agent-product-card__img kodly-ai-agent-product-card__img--hover"
          src={images[1]}
          alt=""
          aria-hidden="true"
          data-visible={shown && loaded ? "true" : "false"}
          onLoad={() => setLoaded(true)}
          onError={() => setSwapFailed(true)}
        />
      )}
    </>
  );
}

// A copia do cartao segue a lingua do WIDGET, nao a do backoffice. O cartao e
// uma reproducao do que o cliente viu, e o cliente leu-o na lingua do chat: um
// operador com o backoffice em ingles a ver uma conversa portuguesa deve ler
// "Entrega em 2 a 3 dias uteis", que foi o que o cliente leu, e nao a traducao
// para a lingua dele.
//
// O locale do widget cai para a lingua do backoffice quando nao vem na config,
// e o proprio `t` ja cai para ingles quando um dicionario nao tem a chave,
// portanto um locale que nao tenhamos traduzido degrada em vez de rebentar.
function useCardTranslate(locale) {
  const { language } = useLanguage();
  const resolved = locale || language;
  return (key, variables) => {
    // As linguas que o widget fala e o backoffice nao, primeiro: sem isto o
    // `t()` responde em ingles a um locale que nao conhece, em vez de cair na
    // lingua do operador, e o cartao de um chat frances ficava a dizer "VIEW
    // PRODUCT" no meio de uma consola em portugues. Ver cardLocales.js.
    const fromCardLocale = cardLocales[resolved]?.[key];
    if (fromCardLocale) return interpolate(fromCardLocale, variables);
    return t(key, resolved, variables);
  };
}

function ProductCard({ item, locale, priceInfoNotice, viewCta }) {
  const translate = useCardTranslate(locale);
  const [imgFailed, setImgFailed] = useState(false);
  const showPlaceholder = !item.image_url || imgFailed;
  const hasReference =
    item.original_price && item.original_price !== item.price;
  // The distinction that must never collapse: a recommended price is the
  // manufacturer's PVPR, a reference, so it carries a label and is NOT struck
  // through. Anything else, "previous" or an absent kind, is a real earlier
  // Radio Popular price and IS struck.
  const isRecommended = item.original_price_kind === "recommended";
  const hasLink = !!item.cta_url;

  // Expiry is re-evaluated at render time, never at flatten time, see
  // activeBadges in utils/productCards.js.
  const badges = activeBadges(item.badges);
  // The two surfaces carry different facts: the tag chip on the top edge
  // (campaign, leaflet, state and unknown kinds) and the discount percentage
  // on the photo's corner. `find`, not `filter`, for the second one: the
  // backend emits at most one price kind per card.
  const overlayBadges = badges.filter((b) => !isPriceBadge(b));
  const priceBadge = badges.find(isPriceBadge);

  // Second photo on hover: only when the item actually carries one and the
  // primary image is really there, since a placeholder has nothing to swap
  // away from.
  const hoverSwap =
    !!item.images && item.images.length > 1 && !showPlaceholder;

  // Availability wins over a delivery estimate when both arrive (a cached card
  // can predate the backend suppressing the estimate for these states): the
  // line must never promise delivery for a product that cannot ship.
  const availabilityCopyKey =
    item.availability !== undefined
      ? AVAILABILITY_COPY_KEYS[item.availability]
      : undefined;

  // ONE chip straddling the card's top left edge, Worten style: zero added
  // height, so badge-less siblings in the same row stay perfectly aligned.
  // Only the most important badge shows, [0] of the backend's importance
  // ordering AFTER the expiry filter. The backend deliberately ships up to 2
  // so a first badge that expires degrades to the runner up, not to nothing.
  const topBadge = overlayBadges[0];

  const media = showPlaceholder ? (
    <div className="kodly-ai-agent-product-card__img kodly-ai-agent-product-card__img--placeholder" />
  ) : hoverSwap ? (
    <HoverSwapImage
      images={item.images}
      title={item.title}
      onError={() => setImgFailed(true)}
    />
  ) : (
    <img
      className="kodly-ai-agent-product-card__img"
      src={item.image_url}
      alt={item.title}
      onError={() => setImgFailed(true)}
    />
  );

  // Media plus title are one target, so clicking the image OR the title opens
  // the product page. In the widget this is a button handing the URL to the
  // host page; here it is a plain link to the same page in a new tab, because
  // the operator is not browsing the shop, they are checking what was shown.
  const mainBody = (
    <>
      <div className="kodly-ai-agent-product-card__media">{media}</div>
      {/* A marca por cima do titulo e dentro do alvo de clique: e identidade do
          produto, nao informacao sobre ele. Mesma posicao que no widget. */}
      {item.brand && (
        <div className="kodly-ai-agent-product-card__brand">{item.brand}</div>
      )}
      <div className="kodly-ai-agent-product-card__title">{item.title}</div>
    </>
  );

  return (
    <article className="kodly-ai-agent-product-card">
      {topBadge && (
        <div className="kodly-ai-agent-product-card__badges">
          <span
            className="kodly-ai-agent-product-card__badge"
            style={badgeStyle(topBadge.kind)}
          >
            {topBadge.label}
          </span>
        </div>
      )}
      {/* The percentage rides the photo's top right corner: the card's two
       * corners then carry two different kinds of fact, campaign phrasing on
       * the left and price maths on the right, and the price block below is
       * left with one short reference line that can never wrap, so a PVPR card
       * and a struck price card keep identical line geometry. Card level, not
       * nested in __media, so the link's label can never swallow its text. */}
      {priceBadge && (
        <span
          className="kodly-ai-agent-product-card__price-badge"
          style={badgeStyle(priceBadge.kind)}
        >
          <span className="kodly-ai-agent-product-card__price-badge-value">
            {priceBadge.label}
          </span>
          {/* Context line per kind, mirroring radiopopular.pt's own boxes:
           * "sobre PVPR" because a bare PVPR relative percentage would claim a
           * price reduction it legally is not, "DIRETO" because the site
           * distinguishes an immediate cut from coupon credit. */}
          {PRICE_BADGE_CONTEXT_KEYS[priceBadge.kind] && (
            <span className="kodly-ai-agent-product-card__price-badge-context">
              {translate(PRICE_BADGE_CONTEXT_KEYS[priceBadge.kind])}
            </span>
          )}
        </span>
      )}
      {hasLink ? (
        <a
          className="kodly-ai-agent-product-card__main"
          href={item.cta_url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${translate("productCardViewProduct")}: ${item.title}`}
        >
          {mainBody}
        </a>
      ) : (
        <div className="kodly-ai-agent-product-card__main kodly-ai-agent-product-card__main--static">
          {mainBody}
        </div>
      )}
      {/* Os tamanhos ficam com a descricao e nao com o preco, como na grelha
          do tenant de onde isto veio. O `title` leva a lista inteira porque o
          CSS corta esta linha a uma so: uma corrida de tamanhos cortada nao e
          como um titulo cortado, quem procura o 44 em "36, 38, 40, 42..."
          conclui que esgotou. */}
      {item.sizes && item.sizes.length > 0 && (
        <div
          className="kodly-ai-agent-product-card__sizes"
          title={`${translate("productCardSizesLabel")} ${item.sizes.join(", ")}`}
        >
          {translate("productCardSizesLabel")} {item.sizes.join(", ")}
        </div>
      )}
      {availabilityCopyKey && (
        <div className="kodly-ai-agent-product-card__availability">
          {translate(availabilityCopyKey)}
        </div>
      )}
      {!availabilityCopyKey && item.delivery && (
        <div className="kodly-ai-agent-product-card__delivery">
          {deliveryText(item.delivery, translate)}
        </div>
      )}
      <div className="kodly-ai-agent-product-card__price-row">
        <div className="kodly-ai-agent-product-card__prices">
          {hasReference &&
            // A etiqueta do tenant e vista PRIMEIRO, antes da copy da casa para
            // o kind. Pela outra ordem ela desaparecia justamente no caso que
            // mais precisa dela, o `recommended`: um tenant manda uma etiqueta
            // porque nenhuma chave nossa diz o que a grelha dele diz.
            (item.original_price_label ? (
              // O embrulho so existe no caminho COM etiqueta, para um tenant
              // sem ela ficar com o DOM que ja tinha e o CSS dele escrito
              // contra __price-original continuar a encontrar o mesmo.
              // A etiqueta fica FORA do riscado porque so o valor e que e
              // riscado: "Prix boutique : 33,00€" com um traco por cima das
              // palavras lia-se como texto retirado e nao como preco anterior.
              <span className="kodly-ai-agent-product-card__price-reference">
                <span className="kodly-ai-agent-product-card__price-label">
                  {item.original_price_label}
                </span>{" "}
                <span
                  className={
                    isRecommended
                      ? "kodly-ai-agent-product-card__price-recommended"
                      : "kodly-ai-agent-product-card__price-original"
                  }
                >
                  <PriceText value={item.original_price} />
                </span>
              </span>
            ) : isRecommended ? (
              <span className="kodly-ai-agent-product-card__price-recommended">
                {translate("productCardRecommendedPriceLabel")}{" "}
                <PriceText value={item.original_price} />
              </span>
            ) : (
              <span className="kodly-ai-agent-product-card__price-original">
                <PriceText value={item.original_price} />
              </span>
            ))}
          {item.price && (
            <span className="kodly-ai-agent-product-card__price">
              <PriceText value={item.price} />
              {priceInfoNotice && (
                <InfoPopover
                  text={priceInfoNotice}
                  triggerClass="kodly-ai-agent-product-card__price-info"
                  triggerLabel={translate("productCardPriceInfo")}
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="13"
                    height="13"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                </InfoPopover>
              )}
            </span>
          )}
        </div>
      </div>
      {/* O botao com que o tile acaba nos sites dos tenants que o tem. Abre o
          mesmo URL que o corpo do cartao ja abre, redundante de proposito,
          porque e o botao que se procura e o cartao-como-link nao se ve.
          Aqui e um <a> e nao um <button>: o widget entrega o URL a pagina
          anfitria por estar num iframe, e o operador abre noutro separador,
          o mesmo criterio ja escrito para o `__main`.
          A bandeira vem da config do chat, como no widget. O widget esconde o
          botao quando algum cartao do lote tem o carrinho, o que aqui nao se
          pode fazer nem faz falta: o `buy_button_active` e deitado fora na
          traducao do payload de proposito, ver utils/productCards.js. */}
      {viewCta && hasLink && (
        <a
          className="kodly-ai-agent-product-card__view-cta"
          href={item.cta_url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${translate("productCardViewProduct")}: ${item.title}`}
        >
          {translate("productCardViewProduct")}
        </a>
      )}
    </article>
  );
}

/**
 * @param {{ items?: Array<Object>, locale?: string, priceInfoNotice?: string,
 *   viewCta?: boolean }} props
 * items are RAW Chatwoot card items, straight out of a transcript message's
 * `blocks[].payload.items`. The flattening into the shape the card renders
 * lives in utils/productCards.js. priceInfoNotice is the chat's widget config
 * copy (config.copy.priceInfoNotice), fetched once by ConversationDetail and
 * passed down, this component never fetches it itself.
 */
function ProductCardStrip({ items, locale, priceInfoNotice, viewCta }) {
  const translate = useCardTranslate(locale);
  const cards = flattenProductCardItems(items);
  if (cards.length === 0) return null;
  return (
    <div className="kodly-ai-agent-product-strip">
      <CardCarousel
        itemCount={cards.length}
        cardStepPx={PRODUCT_CARD_STEP_PX}
        navLabels={{
          previous: translate("productCardPrevious"),
          next: translate("productCardNext"),
        }}
        ariaLabel={translate("productCardCarouselLabel")}
        // Headroom for the straddling tag chip, applied per TRACK and
        // unconditionally, so every strip has the same geometry whether or not
        // its cards carry badges. The discount chip sits fully inside the card
        // and needs no headroom of its own.
        trackClass="kodly-ai-agent-product-card-track--badges"
      >
        {cards.map((card, i) => (
          <ProductCard
            key={`${card.product_id || "card"}-${i}`}
            item={card}
            locale={locale}
            priceInfoNotice={priceInfoNotice}
            viewCta={viewCta}
          />
        ))}
      </CardCarousel>
    </div>
  );
}

export default ProductCardStrip;
